import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import * as api from '../lib/api'
import { SkeletonBlock } from '../components/Skeleton'
import { whenText } from '../lib/when'
import { HISTORY_LABEL, amountText, categoryLabel, durationText, workSessions, workedMsNow } from '../lib/remediation'
import WorkPill from '../components/WorkPill'
import { WorkActionButtons, WorkActionModal, DONE_TEXT } from '../components/WorkActions'
import SampleTag from '../components/SampleTag'
import { isSampleId } from '../lib/samples'

/**
 * One fix, in full — the accountant's own time record for it: how long
 * they've spent, every work session (start/resume → pause/done), every pause
 * and why, and the whole timeline. The same actions as the list, with the
 * same confirmations. Reached by clicking a card on My Ledgers.
 */

/** `28 Sep, 11:42` — a detail page wants the exact moment, not "2h ago". */
function stamp(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  })
}

export default function MyLedgerDetail() {
  const { id } = useParams()
  const [item, setItem] = useState(null)
  const [busyWith, setBusyWith] = useState(null)
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(null)
  const [toast, setToast] = useState(null)
  const [now, setNow] = useState(() => Date.now())

  const load = useCallback(async () => {
    try {
      const [one, open] = await Promise.all([api.getMyRemediationItem(id), api.listMyRemediationItems({ status: 'open' }).catch(() => null)])
      setItem(one)
      setBusyWith((open?.items ?? []).find((x) => x.status === 'in_progress' && x._id !== id) ?? null)
      setError(null)
    } catch (e) {
      setError(e.message)
    }
  }, [id])

  useEffect(() => {
    load()
  }, [load])

  // Live while it's running or paused — both have a clock ticking.
  const ticking = item?.status === 'in_progress' || item?.status === 'paused'
  useEffect(() => {
    if (!ticking) return undefined
    const t = setInterval(() => setNow(Date.now()), 15_000)
    return () => clearInterval(t)
  }, [ticking])

  const record = useMemo(() => (item ? workSessions(item, now) : null), [item, now])

  if (error && !item) {
    return (
      <section className="screen">
        <Link to="/my-ledgers" className="rm-back">‹ My Ledgers</Link>
        <div className="note">{error}</div>
      </section>
    )
  }

  if (!item) {
    return (
      <section className="screen">
        <Link to="/my-ledgers" className="rm-back">‹ My Ledgers</Link>
        <SkeletonBlock height={120} style={{ borderRadius: 8, marginBottom: 16 }} />
        <SkeletonBlock height={220} style={{ borderRadius: 8 }} />
      </section>
    )
  }

  const worked = workedMsNow(item, now)
  const done = item.status === 'confirmed'
  const turnaround = done ? new Date(item.confirmedAt) - new Date(item.createdAt) : now - new Date(item.createdAt).getTime()

  return (
    <section className="screen">
      <Link to="/my-ledgers" className="rm-back">‹ My Ledgers</Link>

      <div className="rm-detail-head">
        <div>
          <div className="rm-detail-id">
            <h1 className="page-title" style={{ margin: 0 }}>{item.clientCode}</h1>
            <SampleTag show={isSampleId(item._id)} />
            <span className="rm-cat">{categoryLabel(item.category)}</span>
            {item.originalAccountantEmail && (
              <span className="rm-covering">Covering for {item.originalAccountantName ?? item.originalAccountantEmail}</span>
            )}
            <WorkPill status={item.status} />
          </div>
          <div className="page-sub">
            {amountText(item.amount) ? `${amountText(item.amount)} · ` : ''}
            {item.reference ? `Ref ${item.reference} · ` : ''}Raised {whenText(item.createdAt)}
          </div>
        </div>
        <WorkActionButtons item={item} onAction={(i, action) => setPending({ item: i, action })} busyWith={busyWith} />
      </div>

      <div className="rm-quote" style={{ marginBottom: 16 }}>{item.description}</div>

      {item.status === 'paused' && (
        <div className="rm-paused" style={{ marginBottom: 16 }}>
          Paused {whenText(item.pausedAt)}
          {item.pauseNote && <> — {item.pauseNote}</>}
        </div>
      )}

      <div className="stats rm-detail-stats">
        <div className="stat">
          <div className="stat-accent" style={{ background: 'var(--blue)' }} />
          <div className="stat-label">Time on it</div>
          <div className={`stat-value${item.status === 'in_progress' ? ' rm-worked' : ''}`}>{worked > 0 ? durationText(worked) : '—'}</div>
          <div className="stat-meta">{item.status === 'in_progress' ? 'running now' : 'working time, pauses excluded'}</div>
        </div>
        <div className="stat">
          <div className="stat-accent" style={{ background: 'var(--amber)' }} />
          <div className="stat-label">Paused for</div>
          <div className="stat-value">{record.pausedMs > 0 ? durationText(record.pausedMs) : '—'}</div>
          <div className="stat-meta">{record.pauses.length} pause{record.pauses.length === 1 ? '' : 's'}</div>
        </div>
        <div className="stat">
          <div className="stat-accent" style={{ background: 'var(--ink-mute)' }} />
          <div className="stat-label">Sessions</div>
          <div className="stat-value">{record.sessions.length || '—'}</div>
          <div className="stat-meta">{item.startedAt ? `first started ${stamp(item.startedAt)}` : 'not started yet'}</div>
        </div>
        <div className="stat">
          <div className="stat-accent" style={{ background: done ? 'var(--green)' : 'var(--red)' }} />
          <div className="stat-label">{done ? 'Raised → done' : 'Open for'}</div>
          <div className="stat-value">{durationText(turnaround)}</div>
          <div className="stat-meta">{done ? `finished ${stamp(item.confirmedAt)}` : `since ${stamp(item.createdAt)}`}</div>
        </div>
      </div>

      <div className="rm-detail-grid">
        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Work sessions</span>
          </div>
          {record.sessions.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>No work recorded yet — press Start when you pick it up.</div>
          ) : (
            <div className="tbl" style={{ border: 'none', borderRadius: 0 }}>
              <div className="thead rm-sess-row">
                <span>#</span>
                <span>From</span>
                <span>To</span>
                <span style={{ textAlign: 'right' }}>Duration</span>
              </div>
              {record.sessions.map((x, i) => (
                <div className="rowgrid rm-sess-row" key={i}>
                  <span className="c-sub" style={{ marginTop: 0 }}>{i + 1}</span>
                  <span>
                    {stamp(x.from)}
                    <span className="c-sub">{x.kind === 'start' ? 'started' : 'resumed'}</span>
                  </span>
                  <span>
                    {x.to ? stamp(x.to) : <span className="rm-worked">running</span>}
                    {x.to && <span className="c-sub">{x.endedBy === 'done' ? 'marked done' : 'paused'}</span>}
                  </span>
                  <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{durationText(x.ms)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-head">
            <span className="panel-title">Pauses</span>
          </div>
          {record.pauses.length === 0 ? (
            <div className="empty" style={{ padding: 20 }}>Never paused.</div>
          ) : (
            <div className="tbl" style={{ border: 'none', borderRadius: 0 }}>
              <div className="thead rm-pause-row">
                <span>Paused</span>
                <span>Resumed</span>
                <span>For</span>
                <span>Why</span>
              </div>
              {record.pauses.map((p, i) => (
                <div className="rowgrid rm-pause-row" key={i}>
                  <span>{stamp(p.from)}</span>
                  <span>{p.to ? stamp(p.to) : <span className="rm-paused-now">still paused</span>}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{durationText(p.ms)}</span>
                  <span className="rm-desc-cell">{p.note || <span className="mute">—</span>}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="panel" style={{ marginTop: 16 }}>
        <div className="panel-head">
          <span className="panel-title">Timeline</span>
        </div>
        <div className="panel-pad">
          <ol className="rm-timeline">
            <li>
              <span className="t-dot" />
              <span>
                <b>Raised</b> {stamp(item.createdAt)}
                {item.channelName && <> · posted to #{item.channelName}</>}
              </span>
            </li>
            {(item.history ?? []).map((h, i) => (
              <li key={i}>
                <span className={`t-dot ${h.action}`} />
                <span>
                  <b>{HISTORY_LABEL[h.action] ?? h.action}</b> {stamp(h.at)}
                  {h.note && <> — “{h.note}”</>}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {pending && (
        <WorkActionModal
          item={pending.item}
          action={pending.action}
          onClose={() => setPending(null)}
          onDone={(i, action) => {
            setPending(null)
            setToast(DONE_TEXT[action](i))
            setTimeout(() => setToast(null), 3500)
            load()
          }}
        />
      )}

      {toast && (
        <div className="toast-wrap">
          <div className="toast toast-ok">{toast}</div>
        </div>
      )}
    </section>
  )
}
